import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

// ═══════════════════════════════════════════════════════════════
// E-023 — S3/R2 import source config hook. Mirrors useHotFolders.
// The secretAccessKey is write-only: the API never returns it, so the
// client type omits it. On create it's required; on edit it's optional
// (omitting it keeps the stored secret).
// ═══════════════════════════════════════════════════════════════

export interface S3ImportConfig {
  id: string;
  tenantId: string;
  name: string;
  bucket: string;
  prefix: string;
  processedPrefix: string;
  region: string | null;
  endpoint: string | null;
  accessKeyId: string;
  forcePathStyle: boolean;
  jobTypeId: string | null;
  declaredFormId: string | null;
  routeByContent: boolean;
  pollingIntervalSec: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface S3ImportCreateInput {
  name: string;
  bucket: string;
  prefix?: string;
  processedPrefix?: string;
  region?: string | null;
  endpoint?: string | null;
  accessKeyId: string;
  secretAccessKey: string;
  forcePathStyle?: boolean;
  jobTypeId?: string | null;
  routeByContent?: boolean;
  pollingIntervalSec?: number;
}

export type S3ImportUpdateInput = Partial<S3ImportCreateInput & { isEnabled: boolean }>;

export interface S3ImportTestResult {
  ok: boolean;
  message: string;
}

export function useS3Imports() {
  const [imports, setImports] = useState<S3ImportConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<{ configs: S3ImportConfig[] }>("/s3-imports");
      setImports(data.configs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (data: S3ImportCreateInput): Promise<S3ImportConfig> => {
    const result = await api.post<S3ImportConfig>("/s3-imports", data);
    await load();
    return result;
  };

  const update = async (id: string, data: S3ImportUpdateInput): Promise<S3ImportConfig> => {
    const result = await api.put<S3ImportConfig>(`/s3-imports/${id}`, data);
    await load();
    return result;
  };

  const remove = async (id: string): Promise<void> => {
    await api.delete(`/s3-imports/${id}`);
    await load();
  };

  const toggle = async (id: string): Promise<S3ImportConfig> => {
    const result = await api.post<S3ImportConfig>(`/s3-imports/${id}/toggle`, {});
    await load();
    return result;
  };

  const test = async (
    data: Partial<S3ImportCreateInput> & { id?: string },
  ): Promise<S3ImportTestResult> => {
    return api.post<S3ImportTestResult>("/s3-imports/test", data);
  };

  return { imports, isLoading, error, refresh: load, create, update, remove, toggle, test };
}
